import { Capacitor } from '@capacitor/core';
import { HealthConnect as HealthConnectPlugin } from '../plugins/health-connect/src/index';
import type { NutritionRecord as HealthConnectNutritionRecord } from '../plugins/health-connect/src/definitions';
import type { Nutrition } from '@macro-tracker/models';
import type { WeightEntry } from './goalsService';
import type { MealId } from '@macro-tracker/models';
import { captureDeveloperLog } from './developerLogging';

const HealthConnect = Capacitor.getPlatform() === 'android' ? HealthConnectPlugin : null;

export interface HealthConnectStatus {
  available: boolean;
  readWeight: boolean;
  writeNutrition: boolean;
}

export interface HealthConnectConfig {
  enabled: boolean;
  syncWeight: boolean;
  syncNutrition: boolean;
  lastWeightSync?: string;
  lastNutritionSync?: string;
}

const CONFIG_KEY = 'healthConnectConfig';

class HealthConnectService {
  private status: HealthConnectStatus = {
    available: false,
    readWeight: false,
    writeNutrition: false,
  };
  private config: HealthConnectConfig = {
    enabled: false,
    syncWeight: true,
    syncNutrition: true,
  };
  private listeners: Set<(status: HealthConnectStatus) => void> = new Set();

  async init(): Promise<void> {
    if (!HealthConnect) {
      captureDeveloperLog('info', '[HealthConnect] not running on Android, skipping init');
      return;
    }

    await this.loadConfig();
    await this.checkAvailability();
    captureDeveloperLog('info', '[HealthConnect] init: available =', this.status.available);
    if (this.status.available) {
      await this.checkPermissions();
      captureDeveloperLog('info', '[HealthConnect] init: readWeight =', this.status.readWeight, 'writeNutrition =', this.status.writeNutrition);
    }
  }

  private async loadConfig(): Promise<void> {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: CONFIG_KEY });
      if (value) {
        this.config = { ...this.config, ...JSON.parse(value) };
      }
    } catch (error) {
      console.error(`Error while loading config ${error}`);
    }
  }

  private async saveConfig(): Promise<void> {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({
        key: CONFIG_KEY,
        value: JSON.stringify(this.config),
      });
    } catch (error) {
      console.error(`Error while loading config ${error}`);
    }
  }

  async checkAvailability(): Promise<boolean> {
    if (!HealthConnect) {
      this.status.available = false;
      return false;
    }

    try {
      const result = await HealthConnect.isAvailable();
      this.status.available = result.available;
      this.notifyListeners();
      return result.available;
    } catch (e) {
      captureDeveloperLog('error', '[HealthConnect] checkAvailability failed', e);
      this.status.available = false;
      this.notifyListeners();
      return false;
    }
  }

  async checkPermissions(): Promise<HealthConnectStatus> {
    if (!HealthConnect || !this.status.available) {
      return this.status;
    }

    try {
      const result = await HealthConnect.checkPermissions();
      this.status.readWeight = result.readWeight;
      this.status.writeNutrition = result.writeNutrition;
      this.notifyListeners();
      return this.status;
    } catch (e) {
      captureDeveloperLog('error', '[HealthConnect] checkPermissions failed', e);
      return this.status;
    }
  }

  async requestPermissions(): Promise<HealthConnectStatus> {
    if (!HealthConnect || !this.status.available) {
      captureDeveloperLog('warn', '[HealthConnect] requestPermissions skipped: available =', this.status.available);
      return this.status;
    }

    captureDeveloperLog('info', '[HealthConnect] requesting permissions');
    try {
      const result = await HealthConnect.grantPermissions({
        readWeight: true,
        writeNutrition: true,
      });
      captureDeveloperLog('info', '[HealthConnect] grantPermissions result', result);
      this.status.readWeight = result.readWeight;
      this.status.writeNutrition = result.writeNutrition;
      this.notifyListeners();
      return this.status;
    } catch (e) {
      captureDeveloperLog('error', '[HealthConnect] grantPermissions failed', e);
      return this.status;
    }
  }

  async readWeight(since: Date): Promise<Partial<WeightEntry>[]> {
    if (!HealthConnect || !this.status.available || !this.status.readWeight) {
      return [];
    }

    try {
      const endTime = new Date().toISOString();
      const startTime = since.toISOString();

      const result = await HealthConnect.readWeight({
        startTime,
        endTime,
      });

      const now = new Date().toISOString();
      return result.records.map((record) => ({
        weightKg: record.weight.value / 1000,
        measuredAtUtc: record.time,
        localDate: record.time.split('T')[0],
        source: 'healthConnect',
        createdAtUtc: now,
        updatedAtUtc: now,
      }));
    } catch {
      return [];
    }
  }

  async writeNutrition(params: {
    date: string;
    mealId: MealId;
    mealName: string;
    nutrition: Nutrition;
  }): Promise<boolean> {
    if (!HealthConnect || !this.status.available || !this.status.writeNutrition) {
      return false;
    }

    try {
      const startTime = new Date(params.date);
      const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);

      const mealTypeMap: Record<number, number> = {
        0: 1,
        1: 2,
        2: 3,
        3: 4,
      };

      await HealthConnect.insertNutrition({
        records: [
          {
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            name: params.mealName,
            mealType: mealTypeMap[params.mealId] ?? 0,
            energy: {
              value: params.nutrition.calories,
              unit: 'kilocalories',
            },
            protein: params.nutrition.protein
              ? { value: params.nutrition.protein, unit: 'grams' }
              : undefined,
            totalCarbohydrate: params.nutrition.carbs
              ? { value: params.nutrition.carbs, unit: 'grams' }
              : undefined,
            totalFat: params.nutrition.fat
              ? { value: params.nutrition.fat, unit: 'grams' }
              : undefined,
          },
        ],
      });

      this.config.lastNutritionSync = new Date().toISOString();
      await this.saveConfig();

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Writes a batch of nutrition records to Health Connect. Each record should
   * carry a stable `metadata.clientRecordId` so repeated syncs upsert instead of
   * creating duplicates. Returns true when the write was attempted successfully.
   */
  async writeNutritionRecords(records: HealthConnectNutritionRecord[]): Promise<boolean> {
    if (!HealthConnect || !this.status.available || !this.status.writeNutrition) {
      return false;
    }
    if (records.length === 0) {
      return true;
    }

    try {
      await HealthConnect.insertNutrition({ records });
      return true;
    } catch (e) {
      captureDeveloperLog('error', '[HealthConnect] writeNutritionRecords failed', e);
      return false;
    }
  }

  getStatus(): HealthConnectStatus {
    return { ...this.status };
  }

  getConfig(): HealthConnectConfig {
    return { ...this.config };
  }

  async updateConfig(config: Partial<HealthConnectConfig>): Promise<void> {
    this.config = { ...this.config, ...config };
    await this.saveConfig();
  }

  onStatusChange(listener: (status: HealthConnectStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener({ ...this.status }));
  }

  isEnabled(): boolean {
    return this.config.enabled;
  }

  async enable(): Promise<void> {
    await this.updateConfig({ enabled: true });
    if (!this.status.readWeight || !this.status.writeNutrition) {
      await this.requestPermissions();
    }
  }

  async disable(): Promise<void> {
    await this.updateConfig({ enabled: false });
  }
}

export const healthConnectService = new HealthConnectService();
